/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Toolbar,
  InputAdornment,
  Alert,
  Snackbar,
  Popover,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  CircularProgress,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Topheader from "../../../Layout/header/header";
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ArrowDropUpRounded,
} from "@mui/icons-material";
import "./domain-approver-applicationList.css";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { fetchDomainApproverContents } from "../../../Services/redux/contents/ContentsDetailsActions";
import { fetchDeletedApplications } from "../../../Services/redux/DeletedApplications/DeletedApplicationsActions";
import { DrawerFilter } from "../../../Components/ApplicationStatistics/Developer/DrawerFilter";
import { fetchDomainApproverCountriesDetails } from "../../../Services/redux/domain-approver-countries/DomainApproverCountryActions";
import { fetchDomainApproverEcuDetails } from "../../../Services/redux/domain-approver-ecu-types/DomainApproverEcuActions";
import { fetchCognitoUsers } from "../../../Services/redux/cognito-users/CognitoUsersActions";
import { fetchApplicationsByStatus, fetchAllStatusCounts, loadInitialData, deleteApplication, rejectDeleteRequest } from "../../../Services/redux/domain-approver-applicationList/DomainApproverApplicationListActions";

import styles from "../../../Components/Table.module.css";

import { getConfig } from "../../../config";

const createEcuEntry = (ecuType, make) => ({
  name: ecuType,
  ecuType: ecuType,
  make: make,
  toString: () => ecuType,
});

const extractUserIds = (user) => {
  const userIds = [];
  if (user.id) {
    userIds.push(user.id);
    if (user.id.includes("_")) {
      const afterUnderscore = user.id.split("_")[1];
      if (afterUnderscore) userIds.push(afterUnderscore);
    }
  }
  if (user.userId) userIds.push(user.userId);
  if (user.cognitoUserId) userIds.push(user.cognitoUserId);
  if (user.username) userIds.push(user.username);
  if (user.userName) userIds.push(user.userName);
  if (user.preferredUserName) userIds.push(user.preferredUserName);
  return userIds;
};

const registerUserInMap = (map, userIds, firstName) => {
  userIds.forEach((id) => {
    if (id) {
      map.set(id, firstName);
      map.set(id.toLowerCase(), firstName);
    }
  });
};

const getSecureRandomId = () => {
  const array = new Uint32Array(4);
  globalThis.crypto.getRandomValues(array);
  return Array.from(array, (dec) => dec.toString(16)).join("");
};

const extractPublisherIds = (data) => {
  const publisherIds = new Set();
  data.forEach((app) => {
    const id = app.publisherId || app.sellerId || app.createdBy;
    if (id) publisherIds.add(id);
    const submissions = Array.isArray(app.submissions)
      ? app.submissions
      : [app.submissions].filter(Boolean);
    submissions.forEach((submission) => {
      const subId = submission.publisherId || submission.sellerId || submission.createdBy;
      if (subId) publisherIds.add(subId);
    });
  });
  return Array.from(publisherIds);
};

const getAppCountries = (app) => {
  if (app.currentSubmission?.countries && Array.isArray(app.currentSubmission.countries)) {
    return app.currentSubmission.countries;
  }
  if (app.submissions && Array.isArray(app.submissions)) {
    return app.submissions.flatMap((s) =>
      Array.isArray(s.countries) ? s.countries : []
    );
  }
  if (app.countries && Array.isArray(app.countries)) return app.countries;
  return [];
};

const getAppEcus = (app) => {
  if (app.currentSubmission?.ecuTypes && Array.isArray(app.currentSubmission.ecuTypes)) {
    return app.currentSubmission.ecuTypes;
  }
  if (app.submissions && Array.isArray(app.submissions)) {
    return app.submissions.flatMap((s) =>
      Array.isArray(s.ecuTypes) ? s.ecuTypes : []
    );
  }
  if (app.ecuTypes && Array.isArray(app.ecuTypes)) return app.ecuTypes;
  return [];
};

const countryMatchesValue = (appCountry, countryValue) => {
  const v =
    typeof appCountry === "object"
      ? appCountry.iso3Code || appCountry.countryCode || appCountry.code
      : appCountry;
  return v === countryValue;
};

const ecuMatchesValue = (appEcu, ecuValue) => {
  const v = typeof appEcu === "object" ? appEcu.name || appEcu.ecuType : appEcu;
  return v === ecuValue;
};

const appMatchesCountry = (app, country) => {
  const countryValue = typeof country === "object" ? country.iso3Code : country;
  return getAppCountries(app).some((c) => countryMatchesValue(c, countryValue));
};

const appMatchesEcu = (app, ecu) => {
  const ecuValue = typeof ecu === "object" ? ecu.name || ecu.ecuType : ecu;
  return getAppEcus(app).some((e) => ecuMatchesValue(e, ecuValue));
};

const getTruncatedTitle = (title, columnWidth) => {
  const isNarrow = columnWidth < 175;
  const maxLen = isNarrow ? 17 : 25;
  if (title.length <= maxLen) return title;
  return `${title.slice(0, maxLen)}...`;
};

const NoRowsOverlay = ({ showNoAppImage }) => (
  <Box height="100%" display="flex" alignItems="center" justifyContent="center">
    <Typography variant="body2" color="textSecondary">
      {showNoAppImage ? "No applications found." : "Loading..."}
    </Typography>
  </Box>
);

NoRowsOverlay.propTypes = {
  showNoAppImage: PropTypes.bool,
};

const renderAppNameCell = (params) => {
  const { row } = params;

  if (row.isChild) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", marginLeft: "20px" }}>
        <Typography sx={{ fontSize: "14px", marginLeft: "5px", color: "#5A5B5C" }} />
      </Box>
    );
  }

  const columnWidth = params.colDef.computedWidth;
  const title = row.applicationName || "Untitled";
  const titleDisplay = getTruncatedTitle(title, columnWidth);
  const shouldTruncateTitle = titleDisplay !== title;

  return (
    <Box sx={{ display: "flex", gap: "2px", flexDirection: "column" }}>
      <Tooltip
        title={shouldTruncateTitle ? title : ""}
        placement="top"
        arrow
        disableInteractive
        disableHoverListener={!shouldTruncateTitle}
      >
        <Typography
          sx={{
            fontSize: "14px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            cursor: "pointer",
            fontWeight: "600",
            marginLeft: "5px",
            color: "#2A2C2D",
          }}
        >
          {titleDisplay}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export default function DomainApproverApplicationList() {
  const [allPublisherIds, setAllPublisherIds] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { domainApproverContents, domainApproverLoading, domainApproverError } =
    useSelector((state) => state.contentAppList);

  // Debug Redux state
  useEffect(() => { }, [
    domainApproverContents,
    domainApproverLoading,
    domainApproverError,
  ]);

  // Deleted applications Redux state
  const { deletedApplications, loading: deletedApplicationsLoading } =
    useSelector((state) => state.deletedApplications);

  // Filter data Redux state
  const countries = useSelector(
    (state) => state.domainApproverCountries.countries
  );
  const ecuTypeData = useSelector((state) => state.domainApproverEcuTypes.ecu);

  // Cognito users Redux state
  const { users: cognitoUsers, loading: cognitoUsersLoading } = useSelector(
    (state) => state.cognitoUsers || { users: [], loading: false }
  );

  const {
    applications,
    counts: groupAppsCounts,
    rejectLoading,
  } = useSelector(
    (state) => state.applications || {
      applications: [],
      loading: false,
      error: null,
      counts: {
        SUBMITTED_FOR_BETA: 0,
        APPROVED_FOR_BETA: 0,
        SUBMITTED_FOR_STAGING: 0,
        APPROVED_FOR_STAGING: 0,
        IN_REVIEW: 0,
        SUBMITTED_FOR_PRODUCTION: 0,
        APPROVED_FOR_PRODUCTION: 0,
        PUBLISHED: 0,
        GENERATING_DELTA: 0,
        DELETED: 0,
        DELISTED_BY_SELLER: 0,
        DELISTED_BY_ADMIN: 0,
        DELISTED_BY_APPROVER: 0,
      },
      deleteLoading: false,
      rejectLoading: false,
    }
  );

  // Memoized filter data with safety checks (deep comparison to prevent unnecessary re-renders)
  const memoizedCountries = useMemo(() => {
    return Array.isArray(countries) ? countries : [];
  }, [countries]);

  const memoizedEcu = useMemo(() => {
    if (!Array.isArray(ecuTypeData)) {
      return [];
    }

    const flatEcuTypes = [];

    ecuTypeData.forEach((makeData) => {
      if (makeData.ecuTypes && Array.isArray(makeData.ecuTypes)) {
        makeData.ecuTypes.forEach((ecuType) => {
          if (typeof ecuType === "string") {
            flatEcuTypes.push(createEcuEntry(ecuType, makeData.make));
          }
        });
      }
    });

    // Remove duplicates based on ecuType name to avoid redundancy
    const uniqueEcuTypes = flatEcuTypes.filter(
      (ecu, index, array) =>
        index === array.findIndex((e) => e.ecuType === ecu.ecuType)
    );

    return uniqueEcuTypes;
  }, [ecuTypeData]);

  // Create a mapping of publisherId to firstName
  const publisherNameMap = useMemo(() => {
    const map = new Map();

    // Handle different possible response formats
    let usersArray = [];

    if (Array.isArray(cognitoUsers)) {
      usersArray = cognitoUsers;
    } else if (cognitoUsers && Array.isArray(cognitoUsers.data)) {
      usersArray = cognitoUsers.data;
    } else if (cognitoUsers && typeof cognitoUsers === "object") {
      // If it's an object, try to find an array property
      const possibleArrayKeys = ["users", "items", "results", "content"];
      for (const key of possibleArrayKeys) {
        if (Array.isArray(cognitoUsers[key])) {
          usersArray = cognitoUsers[key];
          break;
        }
      }
    }

    if (usersArray.length > 0) {
      usersArray.forEach((user) => {
        if (!user || typeof user !== "object") return;
        const userIds = extractUserIds(user);
        const firstName = user.firstName;
        if (firstName && userIds.length > 0) {
          registerUserInMap(map, userIds, firstName);
        }
      });
    }

    return map;
  }, [cognitoUsers]);

  // Helper function for enhanced publisher name formatting
  const getEnhancedPublisherName = useCallback((publisherId) => {
    if (!publisherId) return "N/A";

    // If it's a UUID (like 5887cc97-5172-4b0b-b0cd-0fd0056921bf), show as "Unknown Publisher"
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(publisherId)) {
      return "Unknown Publisher";
    }

    // Extract the actual user ID from the publisher ID (remove prefixes if any)
    const name = publisherId.includes("_")
      ? publisherId.split("_")[1] || publisherId.split("_")[0] // Try to get part after underscore, fallback to first part
      : publisherId;

    if (name.length <= 10 && /^[a-zA-Z0-9]+$/.test(name)) {
      return "Unknown Publisher";
    }

    // Capitalize first letter and handle other cases
    const result = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

    return result;
  }, []);

  // Function to get publisher name

  const getPublisherName = useCallback(
    (publisherId) => {
      if (!publisherId) {
        return "N/A";
      }
      if (cognitoUsersLoading && allPublisherIds.length > 0) {
        return "Loading...";
      }

      let firstName = publisherNameMap.get(publisherId);

      if (!firstName) {
        firstName = publisherNameMap.get(publisherId.toLowerCase());
      }

      if (!firstName && publisherId.includes("_")) {
        const afterUnderscore = publisherId.split("_")[1];
        if (afterUnderscore) {
          firstName = publisherNameMap.get(afterUnderscore);

          if (!firstName) {
            firstName = publisherNameMap.get(afterUnderscore.toLowerCase());
          }
        }
      }

      if (firstName) {
        return firstName;
      }

      if (!cognitoUsersLoading && allPublisherIds.length > 0) {
        return getEnhancedPublisherName(publisherId);
      }
    },
    [publisherNameMap, getEnhancedPublisherName]
  );

  // Fetch filter data on component mount
  useEffect(() => {
    dispatch(fetchDomainApproverCountriesDetails());
    dispatch(fetchDomainApproverEcuDetails());
  }, [dispatch]);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedTab, setSelectedTab] = useState(() => {
    const storedTab = sessionStorage.getItem("domain-approver-selectedTab");
    return storedTab === null ? 0 : Number.parseInt(storedTab); // Default to Group Apps tab
  });
  const [activeStatus, setActiveStatus] = useState(() => {
    const storedStatus = sessionStorage.getItem("domain-approver-activeStatus");
    const defaultStatus = "SUBMITTED_FOR_PRODUCTION";
    const finalStatus = storedStatus || defaultStatus;

    // Store the default if nothing was stored
    if (!storedStatus) {
      sessionStorage.setItem("domain-approver-activeStatus", defaultStatus);
    }

    return finalStatus;
  });
  const [activeThirdPartyStatus, setActiveThirdPartyStatus] = useState(() => {
    const storedThirdPartyStatus = sessionStorage.getItem(
      "domain-approver-activeThirdPartyStatus"
    );
    const finalStatus = storedThirdPartyStatus || "DOMAIN_APPROVAL_IN_REVIEW";

    return finalStatus;
  });
  const [thirdPartyApplications, setThirdPartyApplications] = useState([]);

  const [openSections, setOpenSections] = useState(() => {
    const storedOpenSections = sessionStorage.getItem(
      "domain-approver-openSections"
    );
    if (storedOpenSections) {
      return JSON.parse(storedOpenSections);
    }
    return {
      Beta: false,
      Staging: false,
      Production: true,
      Deleted: false,
      Delisted: false,
    };
  });

  // Check idpLogin flag from session storage
  const [idpLogin] = useState(() => {
    const storedIdpLogin = sessionStorage.getItem("idpLogin");
    return storedIdpLogin === "true";
  });

  const [applicationList, setApplicationList] = useState([]);
  const [loader, setLoader] = useState(true);
  const [showNoAppImage, setShowNoAppImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false);
  const [show401Error, setShow401Error] = useState(false);

  const [ecuTypesPopup, setEcuTypesPopup] = useState({
    open: false,
    data: [],
    anchorEl: null,
  });
  const [countriesPopup, setCountriesPopup] = useState({
    open: false,
    data: [],
    anchorEl: null,
  });

  // Logout handler for 401 error UI
  const handleLogout = async () => {
    const clientId =
      sessionStorage.getItem("idpLogin") &&
        sessionStorage.getItem("idpLogin") === "true"
        ? getConfig().REACT_APP_VW_DOMAIN_APPROVER_CLIENT_ID
        : getConfig().REACT_APP_DOMAIN_APPROVER_CLIENT_ID;

    const cognitoDomain = getConfig().REACT_APP_COGNITO_DOMAIN;
    const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;

    if (
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "true"
    ) {
      const revokeURL = `${getConfig().REACT_APP_COGNITO_DOMAIN}/oauth2/revoke`;
      try {
        const body = new URLSearchParams();
        body.append("token", sessionStorage.getItem("access_token"));
        body.append("client_id", clientId);

        await fetch(revokeURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        });
      } catch (error) {
        console.error("Error revoking token:", error);
      }
    }

    // Clear all storage
    sessionStorage.clear();
    localStorage.clear();

    // Redirect to Cognito logout
    const logoutURL = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      redirect_uri
    )}`;
    globalThis.location.href = logoutURL;
  };

  const clickApplications = (clickedRow) => {
    if (selectedTab === 0) {
      // Group Apps
      if (
        clickedRow.row &&
        (clickedRow.row.currentSubmission || clickedRow.row.submissionId)
      ) {
        const applicationId =
          clickedRow.row.originalApplicationId ||
          clickedRow.row.applicationId ||
          (clickedRow.id.includes("group_")
            ? clickedRow.id.replaceAll("group_", "")
            : clickedRow.id.replaceAll(`_submission_child_`, "").split("_")[0]);

        const submissionId =
          clickedRow.row.submissionId ||
          clickedRow.row.currentSubmission?.id ||
          clickedRow.row.id;

        // Show full page loader before navigating to Group Apps details
        setLoader(true);
        navigate(
          `/ui/domain-approver/applications/${applicationId}/submission/${submissionId}`
        );
      }
    } else {
      // 3rd Party Apps (existing logic)
      // Fix for third-party group applications
      let applicationId = clickedRow.id;

      // Remove the thirdparty_child suffix if it exists (for child rows)
      if (applicationId.includes("_thirdparty_child_")) {
        applicationId = applicationId.replace(/_thirdparty_child_\d+$/, "");
      }
      // Remove the thirdparty_group_ prefix if it exists
      else if (applicationId.includes("thirdparty_group_")) {
        applicationId = applicationId.replace("thirdparty_group_", "");
      }
      // Remove the group_ prefix if it exists
      else if (applicationId.includes("group_")) {
        applicationId = applicationId.replace("group_", "");
      }

      // Show full page loader before navigating to 3rd Party Apps details
      setLoader(true);
      navigate(
        `/ui/domain-approver/applications/${applicationId}/third-party-apps`
      );
    }
  };
  // Confirmation dialog state for delete action
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    applicationId: null,
    applicationName: "",
  });

  // Confirmation dialog state for reject action
  const [rejectConfirmDialog, setRejectConfirmDialog] = useState({
    open: false,
    applicationId: null,
    applicationName: "",
  });

  // State for tracking expanded application groups
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // State for tracking expanded third party application groups
  const [expandedThirdPartyGroups, setExpandedThirdPartyGroups] = useState(
    new Set()
  );

  // Group Apps grouping function (by applicationName with submissions)
  const groupApplicationsByApplicationName = useCallback(
    (applications) => {
      const result = [];
      const applicationGroups = {};

      // First, flatten applications with their submissions (GROUP APPS ONLY)
      const flattenedApplications = [];
      applications.forEach((app) => {
        let submissionsArray;
        if (Array.isArray(app.submissions)) {
          submissionsArray = app.submissions;
        } else if (app.submissions) {
          submissionsArray = [app.submissions];
        } else {
          submissionsArray = [];
        }
        if (submissionsArray && submissionsArray.length > 0) {
          // Each submission becomes a separate row
          submissionsArray.forEach((submission) => {
            flattenedApplications.push({
              // Application level data
              id: submission.id,
              applicationId: app.id,
              applicationName: app.applicationName,
              publisherId: app.publisherId,
              iconUrl: app.iconUrl,
              reviewedBy: app.reviewedBy,
              version: app.version,
              domain: app.domain,
              signed: app.signed,

              // Submission level data
              submissionId: submission.id,
              submissionName: submission.name,
              submissionVersion: submission.name,
              createdAt: submission.createdAt,
              lastModified: submission.lastModified,
              publishedAt: submission.publishedAt,
              submittedAt: submission.submittedAt,
              minSdkVersion: submission.minSdkVersion,
              countries: submission.countries,
              ecuTypes: submission.ecuTypes,
              changelog: submission.changelog,

              // Get current status from the latest changelog entry
              status:
                submission.changelog && submission.changelog.length > 0
                  ? submission.changelog[submission.changelog.length - 1]
                    .toState
                  : "DRAFT",
            });
          });
        } else {
          // No submissions - add the application itself
          flattenedApplications.push({
            ...app,
            submissionId: null,
            submissionName: null,
            submissionVersion: app.version,
            status: "DRAFT",
          });
        }
      });

      // Group by applicationName
      flattenedApplications.forEach((app) => {
        const groupKey = app.applicationName || `fallback-${app.id}`;
        if (!applicationGroups[groupKey]) {
          applicationGroups[groupKey] = [];
        }
        applicationGroups[groupKey].push(app);
      });

      // Process each application group
      Object.entries(applicationGroups).forEach(([applicationName, apps]) => {
        const sortedApps = apps.sort((a, b) => {
          if (a.submissionVersion && b.submissionVersion) {
            return b.submissionVersion.localeCompare(
              a.submissionVersion,
              undefined,
              {
                numeric: true,
                sensitivity: "base",
              }
            );
          }
          const dateA = new Date(a.submittedAt || a.createdAt || 0);
          const dateB = new Date(b.submittedAt || b.createdAt || 0);
          return dateB - dateA;
        });

        if (sortedApps.length > 1) {
          // Multiple submissions - create group
          const firstApp = sortedApps[0];
          const groupId = `group_${firstApp.applicationId || firstApp.id}`;
          const isExpanded = expandedGroups.has(groupId);

          result.push({
            ...firstApp,
            id: groupId,
            isGroup: true,
            isExpanded,
            childCount: sortedApps.length - 1,
            originalId: firstApp.id,
            originalApplicationId: firstApp.applicationId,
            totalSubmissions: sortedApps.length,
          });

          if (isExpanded) {
            sortedApps.slice(1).forEach((app, index) => {
              result.push({
                ...app,
                id: `${app.id}_submission_child_${index + 1}`,
                isGroup: false,
                isChild: true,
                originalId: app.id,
                originalApplicationId: app.applicationId,
              });
            });
          }
        } else {
          const singleApp = sortedApps[0];
          result.push({
            ...singleApp,
            id: singleApp.id || singleApp.applicationId || `app_${singleApp.applicationId || getSecureRandomId()}`,
            isGroup: false,
            originalId: singleApp.id,
            originalApplicationId: singleApp.applicationId,
          });
        }
      });

      return result;
    },
    [expandedGroups]
  );

  // Group third party applications by packageId and create hierarchical structure
  const groupThirdPartyApplicationsByPackageId = useCallback(
    (applications) => {
      const result = [];
      const appGroups = {};

      // First, group applications by packageId
      applications.forEach((app) => {
        const packageId =
          app.packageId || `fallback-${app.id || getSecureRandomId()}`;
        if (!appGroups[packageId]) {
          appGroups[packageId] = [];
        }
        appGroups[packageId].push(app);
      });

      // Process each group
      Object.entries(appGroups).forEach(([packageId, apps]) => {
        if (apps.length > 1) {
          // Multiple versions - create group with expand/collapse
          const groupId = `thirdparty_group_${apps[0].id}`;
          const isExpanded = expandedThirdPartyGroups.has(groupId);

          // Add first app as the parent/group row with expand/collapse
          const firstApp = apps[0];
          result.push({
            ...firstApp,
            id: groupId,
            applicationName: firstApp.applicationName || "Unknown",
            isGroup: true,
            isExpanded,
            childCount: apps.length - 1,
            isThirdParty: true,
          });

          // Add remaining apps as children if expanded
          if (isExpanded) {
            apps.slice(1).forEach((app, index) => {
              result.push({
                ...app,
                id: `${app.id}_thirdparty_child_${index + 1}`,
                isGroup: false,
                isChild: true,
                isThirdParty: true,
                groupName: packageId,
                parentId: groupId,
              });
            });
          }
        } else {
          // Single version - add directly without grouping
          const singleApp = apps[0];
          result.push({
            ...singleApp,
            id: singleApp.id || `thirdparty_single_${singleApp.applicationId || getSecureRandomId()}`,
            isGroup: false,
            isThirdParty: true,
          });
        }
      });

      return result;
    },
    [expandedThirdPartyGroups]
  );

  // State for filter drawer
  const [openFilter, setOpenFilter] = useState(false);

  // Memoize toggleDrawer to prevent unnecessary re-renders in DrawerFilter
  const toggleDrawer = useCallback(
    (newOpen) => () => {
      setOpenFilter(newOpen);
    },
    []
  );
  const [filteredApplicationList, setFilteredApplicationList] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    selectedCountries: [],
    selectedEcus: [],
  });

  const isCountsLoadingRef = useRef(false);
  const isApplicationsLoadingRef = useRef(false);
  const isComponentInitializedRef = useRef(false);
  const isContentApiLoadingRef = useRef(false);
  const hasContentDataRef = useRef(false);
  const isNavigatingFromDetailRef = useRef(false);
  const navigationTimeoutRef = useRef(null);

  const handleGroupAppsTab = () => {
    const defaultGroupStatus = "SUBMITTED_FOR_PRODUCTION";
    setActiveStatus(defaultGroupStatus);
    sessionStorage.setItem("domain-approver-activeStatus", defaultGroupStatus);

    const hasGroupCounts = Object.values(groupAppsCounts).some((count) => count > 0);
    if (!hasGroupCounts) {
      loadAllGroupAppsCounts().catch(() => {});
    }

    if (isNavigatingFromDetailRef.current) {
      return;
    }
    getAllApplications(defaultGroupStatus, false);
  };

  const handleThirdPartyTab = () => {
    const defaultThirdPartyStatus = "DOMAIN_APPROVAL_IN_REVIEW";
    setActiveThirdPartyStatus(defaultThirdPartyStatus);
    sessionStorage.setItem("domain-approver-activeThirdPartyStatus", defaultThirdPartyStatus);

    setThirdPartyCounts({
      DOMAIN_APPROVAL_IN_REVIEW: 0,
      DOMAIN_APPROVAL_APPROVED: 0,
      REJECTED_BY_DOMAIN_APPROVER: 0,
    });

    const noContent = !domainApproverContents || domainApproverContents.length === 0;
    if (!domainApproverLoading && !isContentApiLoadingRef.current && noContent) {
      isContentApiLoadingRef.current = true;
      dispatch(fetchDomainApproverContents());
    } else if (!domainApproverLoading && noContent) {
      setLoader(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setLoader(true);
    clearAllFilters();
    sessionStorage.setItem("domain-approver-selectedTab", newValue.toString());

    if (newValue === 0) {
      handleGroupAppsTab();
    } else if (newValue === 1) {
      handleThirdPartyTab();
    }
  };

  const handleEcuTypesClose = () => {
    setEcuTypesPopup({
      open: false,
      data: [],
      anchorEl: null,
    });
  };

  const handleCountriesClose = () => {
    setCountriesPopup({
      open: false,
      data: [],
      anchorEl: null,
    });
  };

  // Handle delete button click
  const handleDeleteClick = (applicationId, applicationName) => {
    setDeleteConfirmDialog({
      open: true,
      applicationId,
      applicationName,
    });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    const { applicationId } = deleteConfirmDialog;
    try {
      const result = await Promise.resolve(dispatch(deleteApplication(applicationId)));

      if (result.success) {
        // Refresh the deleted applications list
        dispatch(fetchDeletedApplications());

        // Refresh counts
        loadAllGroupAppsCounts();

        // Show success message
        setSuccessMessage("Application deleted successfully");
        setOpenSuccessPopup(true);
      } else {
        setErrorMessage(result.error || "Failed to delete application");
        setOpenErrorPopup(true);
      }
    } catch (error) {
      console.error("Failed to delete application:", error);
      setErrorMessage("Failed to delete application");
      setOpenErrorPopup(true);
    } finally {
      setDeleteConfirmDialog({
        open: false,
        applicationId: null,
        applicationName: "",
      });
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({
      open: false,
      applicationId: null,
      applicationName: "",
    });
  };

  // Handle reject button click
  const handleRejectClick = (applicationId, applicationName) => {
    setRejectConfirmDialog({
      open: true,
      applicationId,
      applicationName,
    });
  };

  // Handle reject confirmation
  const handleRejectConfirm = async () => {
    const { applicationId } = rejectConfirmDialog;

    const result = await Promise.resolve(dispatch(rejectDeleteRequest(applicationId)));

    if (result.success) {
      // Refresh the deleted applications list
      dispatch(fetchDeletedApplications());

      // Refresh counts
      dispatch(fetchAllStatusCounts());

      // Show success message
      setSuccessMessage("Rejected delete request successfully");
      setOpenSuccessPopup(true);
    } else {
      setErrorMessage(result.error || "Failed to reject delete request");
      setOpenErrorPopup(true);
    }

    setRejectConfirmDialog({
      open: false,
      applicationId: null,
      applicationName: "",
    });
  };

  // Handle reject cancel
  const handleRejectCancel = () => {
    setRejectConfirmDialog({
      open: false,
      applicationId: null,
      applicationName: "",
    });
  };

  const processApplicationResult = useCallback(
    (result) => {
      if (result.success && result.data?.length > 0) {
        const uniqueIds = extractPublisherIds(result.data);
        setAllPublisherIds(uniqueIds);
        if (uniqueIds.length > 0) {
          dispatch(fetchCognitoUsers());
        }
      }

      if (result.error) {
        if (result.errorObj?.status === 401) {
          setShow401Error(true);
        } else {
          setErrorMessage(result.error);
          setOpenErrorPopup(true);
        }
      } else if (result.success && result.data.length === 0) {
        setShowNoAppImage(true);
      }
    },
    [dispatch]
  );

  const getAllApplications = useCallback(
    async function (status, skipLoader = false) {
      // Ensure status is never undefined - provide default fallback
      const safeStatus = status || "SUBMITTED_FOR_PRODUCTION";

      // Prevent multiple simultaneous calls or calls during navigation
      if (
        isApplicationsLoadingRef.current ||
        isNavigatingFromDetailRef.current
      ) {
        return;
      }

      if (!skipLoader) {
        isApplicationsLoadingRef.current = true;
        setLoader(true);
      }
      setShowNoAppImage(false);
      setErrorMessage("");

      if (safeStatus === "DELETED") {
      dispatch(fetchDeletedApplications());
        if (!skipLoader) {
          isApplicationsLoadingRef.current = false;
          // Turn off loader immediately after dispatch
          setLoader(false);
        }
        return;
      }

      const result = await Promise.resolve(dispatch(fetchApplicationsByStatus(safeStatus)));

      processApplicationResult(result);

      if (!skipLoader && !isNavigatingFromDetailRef.current) {
        isApplicationsLoadingRef.current = false;
        setLoader(false);
      }
    },
    [dispatch, processApplicationResult]
  );

  // Load counts for all Group Apps statuses
  const loadAllGroupAppsCounts = useCallback(async () => {
    if (isCountsLoadingRef.current) {
      return null;
    }

    isCountsLoadingRef.current = true;

    try {
      const counts = await Promise.resolve(dispatch(fetchAllStatusCounts()));

      if (counts) {
        const currentDeletedApps = deletedApplications || [];
        counts.DELETED = currentDeletedApps.length;
      }

      return counts;
    } catch (error) {
      console.error("Failed to load application counts:", error);
      return null;
    } finally {
      isCountsLoadingRef.current = false;
    }
  }, [dispatch, deletedApplications]);

  const [thirdPartyCounts, setThirdPartyCounts] = useState({
    DOMAIN_APPROVAL_IN_REVIEW: 0,
    DOMAIN_APPROVAL_APPROVED: 0,
    REJECTED_BY_DOMAIN_APPROVER: 0,
  });

  const processThirdPartyData = useCallback(() => {
    // Show 0 counts initially while processing
    if (selectedTab === 1) {
      setThirdPartyCounts({
        DOMAIN_APPROVAL_IN_REVIEW: 0,
        DOMAIN_APPROVAL_APPROVED: 0,
        REJECTED_BY_DOMAIN_APPROVER: 0,
      });
    }

    // Log first few items to see data structure
    if (domainApproverContents && domainApproverContents.length > 0) {
      // Add a small delay to ensure users see the loading state
      const groupedByStatus = {
        DOMAIN_APPROVAL_IN_REVIEW: [],
        DOMAIN_APPROVAL_APPROVED: [],
        REJECTED_BY_DOMAIN_APPROVER: [],
      };

      // Group applications by status first
      let statusCounts = {
        approved: 0,
        rejected: 0,
        inReview: 0,
        unknown: 0,
      };

      domainApproverContents.forEach((app, index) => {
        const status = app.domainApprovalStatus;

        // Create a processed application object
        const processedApp = {
          ...app,
          id: app.id || app.applicationId || index + 1,
        };

        if (
          status === "DOMAIN_APPROVAL_APPROVED" ||
          status === "APPROVED_BY_DOMAIN_APPROVER"
        ) {
          groupedByStatus.DOMAIN_APPROVAL_APPROVED.push(processedApp);
          statusCounts.approved++;
        } else if (status === "REJECTED_BY_DOMAIN_APPROVER") {
          groupedByStatus.REJECTED_BY_DOMAIN_APPROVER.push(processedApp);
          statusCounts.rejected++;
        } else if (status === "DOMAIN_APPROVAL_IN_REVIEW") {
          groupedByStatus.DOMAIN_APPROVAL_IN_REVIEW.push(processedApp);
          statusCounts.inReview++;
        } else {
          groupedByStatus.DOMAIN_APPROVAL_IN_REVIEW.push(processedApp);
          statusCounts.inReview++;
          statusCounts.unknown++;
        }
      });

      // Group each status category by application name to get correct counts
      const groupedStatusCounts = {};
      Object.keys(groupedByStatus).forEach((status) => {
        const statusApps = groupedByStatus[status];

        const groupedApps = groupThirdPartyApplicationsByPackageId(statusApps);
        // Count only parent rows (excluding child rows)
        const finalCount = groupedApps.filter((app) => !app.isChild).length;
        groupedStatusCounts[status] = finalCount;
      });

      // Update counts with grouped counts (excluding child rows)
      const newCounts = {
        DOMAIN_APPROVAL_IN_REVIEW:
          groupedStatusCounts.DOMAIN_APPROVAL_IN_REVIEW || 0,
        DOMAIN_APPROVAL_APPROVED:
          groupedStatusCounts.DOMAIN_APPROVAL_APPROVED || 0,
        REJECTED_BY_DOMAIN_APPROVER:
          groupedStatusCounts.REJECTED_BY_DOMAIN_APPROVER || 0,
      };

      setThirdPartyCounts(newCounts);

      const currentStatusApps = groupedByStatus[activeThirdPartyStatus] || [];

      // Group third party applications by packageId similar to Group Apps
      const groupedThirdPartyApps =
        groupThirdPartyApplicationsByPackageId(currentStatusApps);

      setThirdPartyApplications(groupedThirdPartyApps);

      // Turn off loader after successful data processing and counts are set
      // Only turn off loader if we're currently on the 3rd party tab AND the API is not loading
      if (selectedTab === 1 && !domainApproverLoading) {
        setLoader(false);
      }
    } else {
      // Keep counts at 0 and turn off loader after delay
      setThirdPartyApplications([]);
      if (selectedTab === 1 && !domainApproverLoading) {
        setLoader(false);
      }
    }
  }, [
    domainApproverContents,
    activeThirdPartyStatus,
    selectedTab,
    domainApproverLoading,
    groupThirdPartyApplicationsByPackageId,
    setLoader,
  ]);

  // Centralized navigation handler to prevent multiple API calls

  const handleNavigationFromDetailPage = useCallback(async () => {
    isNavigatingFromDetailRef.current = true;

    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    setSelectedTab(0);
    sessionStorage.setItem("domain-approver-selectedTab", "0");

    const currentStatus =
      activeStatus ||
      sessionStorage.getItem("domain-approver-activeStatus") ||
      "SUBMITTED_FOR_PRODUCTION";
    setActiveStatus(currentStatus);
    sessionStorage.setItem("domain-approver-activeStatus", currentStatus);

    setLoader(true);
    setShowNoAppImage(false);
    setErrorMessage("");

    try {
      // ✅ Use Redux Thunk actions instead of direct API calls
      await Promise.all([
        Promise.resolve(dispatch(fetchApplicationsByStatus(currentStatus))),
        Promise.resolve(dispatch(fetchAllStatusCounts())),
      ]);
    } catch (error) {
      console.error("Navigation API calls failed:", error);
      setApplicationList([]);
      setShowNoAppImage(true);
      setErrorMessage("Failed to load applications during navigation");
    } finally {
      isNavigatingFromDetailRef.current = false;
      setLoader(false);
    }
  }, [activeStatus, dispatch]);

  useEffect(() => {
    // Only run initial data loading once
    if (!isComponentInitializedRef.current) {
      isComponentInitializedRef.current = true;
      setLoader(true);

      dispatch(fetchCognitoUsers());
      dispatch(fetchDeletedApplications());
      
      dispatch(loadInitialData(activeStatus))
        .then((result) => {
          console.log("✅ Initial data loaded:", result);
          if (result.error && result.errorObj?.status === 401) {
            setShow401Error(true);
          } else if (result.error) {
            setErrorMessage(result.error);
            setOpenErrorPopup(true);
          } else if (result.applications?.length === 0) {
            setShowNoAppImage(true);
          }
        })
        .catch((error) => {
          console.error("❌ Error loading initial data:", error);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  }, []); // Only run once on component mount

  // Handle navigation state changes (when returning from detail pages)
  useEffect(() => {
    // Check if we navigated back from a detail page and need to refresh
    if (location.state?.refresh || location.state?.forceRefresh) {
      // Use centralized navigation handler that properly coordinates loader
      handleNavigationFromDetailPage();

      // Clear the state to prevent unnecessary reloads
      if (location.state) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [
    location.state,
    navigate,
    location.pathname,
    handleNavigationFromDetailPage,
  ]);

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      isNavigatingFromDetailRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (deletedApplications !== null && Array.isArray(deletedApplications)) {
      // Update the Redux store with the deleted count
      dispatch({
        type: 'UPDATE_DELETED_COUNT',
        payload: deletedApplications.length
      });

      // If we're on deleted tab and loader is on, turn it off now that data is loaded
      if (activeStatus === "DELETED" && loader && !deletedApplicationsLoading) {
        setLoader(false);
      }
    }
  }, [deletedApplications, deletedApplicationsLoading, activeStatus, loader]);

  useEffect(() => {
    // Always fetch deleted applications as they're needed for Group Apps (only once on mount)
    if (!isComponentInitializedRef.current) {
      dispatch(fetchDeletedApplications());
      isComponentInitializedRef.current = true;
    }
  }, [dispatch]);

  // Process 3rd party data when contents change or active status changes
  useEffect(() => {
    if (
      selectedTab === 1 &&
      domainApproverContents &&
      domainApproverContents.length > 0 &&
      !domainApproverLoading
    ) {
      isContentApiLoadingRef.current = false; // Reset loading flag
      hasContentDataRef.current = true; // Mark that we have data
      processThirdPartyData();
    } else if (selectedTab === 1 && domainApproverLoading) {
      // Keep loader on and counts at 0 while loading
      setThirdPartyCounts({
        DOMAIN_APPROVAL_IN_REVIEW: 0,
        DOMAIN_APPROVAL_APPROVED: 0,
        REJECTED_BY_DOMAIN_APPROVER: 0,
      });
      // DON'T turn off loader here - wait for API completion
    } else if (
      selectedTab === 1 &&
      !domainApproverLoading &&
      (!domainApproverContents || domainApproverContents.length === 0)
    ) {
      isContentApiLoadingRef.current = false; // Reset loading flag
      hasContentDataRef.current = false; // Mark that we don't have data
      // Only turn off loader when API is actually done loading (domainApproverLoading is false)
      setLoader(false);
    }
  }, [
    selectedTab,
    domainApproverContents,
    domainApproverLoading,
    activeThirdPartyStatus,
    processThirdPartyData,
  ]);

  // Initialize and update filtered application list
  useEffect(() => {
    if (applications.length > 0) {
      setApplicationList(applications);

      if (appliedFilters.selectedCountries.length > 0 || appliedFilters.selectedEcus.length > 0) {
        const filtered = applyFiltersToApplications(applications, appliedFilters);
        setFilteredApplicationList(filtered);
      } else {
        setFilteredApplicationList(applications);
      }
    }
  }, [applications, appliedFilters]);

  useEffect(() => {
    // Only update sections when switching to 3rd party tab
    if (selectedTab === 1) {
      setOpenSections((prev) => ({
        ...prev,
        Production: true,
      }));
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!isComponentInitializedRef.current) {
      isComponentInitializedRef.current = true;
      setLoader(true);

      dispatch(loadInitialData(activeStatus))
        .then((result) => {
          if (result.error && result.errorObj?.status === 401) {
            setShow401Error(true);
          } else if (result.error) {
            setErrorMessage(result.error);
            setOpenErrorPopup(true);
          } else if (result.applications?.length === 0) {
            setShowNoAppImage(true);
          }
        })
        .finally(() => {
          setLoader(false);
        });
    }
  }, [dispatch, activeStatus]);

  const staticSections = {
    "Group Apps": {
      Beta: [
        {
          status: "SUBMITTED_FOR_BETA",
          text: "Submitted",
          count: groupAppsCounts.SUBMITTED_FOR_BETA,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "APPROVED_FOR_BETA",
          text: "Approved",
          count: groupAppsCounts.APPROVED_FOR_BETA,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.90962 16L4.462 13.5619L1.71914 12.9524L1.98581 10.1333L0.119141 8L1.98581 5.86667L1.71914 3.04762L4.462 2.4381L5.90962 0L8.50009 1.10476L11.0906 0L12.5382 2.4381L15.281 3.04762L15.0144 5.86667L16.881 8L15.0144 10.1333L15.281 12.9524L12.5382 13.5619L11.0906 16L8.50009 14.8952L5.90962 16ZM7.70009 10.7048L12.0049 6.4L10.9382 5.29524L7.70009 8.53333L6.062 6.93333L4.99533 8L7.70009 10.7048Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
      ],
      Staging: [
        {
          status: "SUBMITTED_FOR_STAGING",
          text: "Submitted",
          count: groupAppsCounts.SUBMITTED_FOR_STAGING,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "APPROVED_FOR_STAGING",
          text: "Approved",
          count: groupAppsCounts.APPROVED_FOR_STAGING,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.90962 16L4.462 13.5619L1.71914 12.9524L1.98581 10.1333L0.119141 8L1.98581 5.86667L1.71914 3.04762L4.462 2.4381L5.90962 0L8.50009 1.10476L11.0906 0L12.5382 2.4381L15.281 3.04762L15.0144 5.86667L16.881 8L15.0144 10.1333L15.281 12.9524L12.5382 13.5619L11.0906 16L8.50009 14.8952L5.90962 16ZM7.70009 10.7048L12.0049 6.4L10.9382 5.29524L7.70009 8.53333L6.062 6.93333L4.99533 8L7.70009 10.7048Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
      ],
      Production: [
        {
          status: "IN_REVIEW",
          text: "In Review",
          count: groupAppsCounts.IN_REVIEW,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "SUBMITTED_FOR_PRODUCTION",
          text: "Submitted",
          count: groupAppsCounts.SUBMITTED_FOR_PRODUCTION,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "APPROVED_FOR_PRODUCTION",
          text: "Approved",
          count: groupAppsCounts.APPROVED_FOR_PRODUCTION,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.90962 16L4.462 13.5619L1.71914 12.9524L1.98581 10.1333L0.119141 8L1.98581 5.86667L1.71914 3.04762L4.462 2.4381L5.90962 0L8.50009 1.10476L11.0906 0L12.5382 2.4381L15.281 3.04762L15.0144 5.86667L16.881 8L15.0144 10.1333L15.281 12.9524L12.5382 13.5619L11.0906 16L8.50009 14.8952L5.90962 16ZM7.70009 10.7048L12.0049 6.4L10.9382 5.29524L7.70009 8.53333L6.062 6.93333L4.99533 8L7.70009 10.7048Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "PUBLISHED",
          text: "Published",
          count: groupAppsCounts.PUBLISHED,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "GENERATING_DELTA",
          text: "Generating Delta",
          count: groupAppsCounts.GENERATING_DELTA || 0,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 2L10.5 6H14.5L11.5 9L12.5 13L8.5 11L4.5 13L5.5 9L2.5 6H6.5L8.5 2Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
      ],
      Deleted: [
        {
          status: "DELETED",
          text: "Deleted",
          count: groupAppsCounts.DELETED,
          isCountNeeded: true,
          icon: (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.05534 16C2.56645 16 2.14793 15.8259 1.79978 15.4778C1.45163 15.1296 1.27756 14.7111 1.27756 14.2222V2.66667H0.388672V0.888889H4.83312V0H10.1664V0.888889H14.6109V2.66667H13.722V14.2222C13.722 14.7111 13.5479 15.1296 13.1998 15.4778C12.8516 15.8259 12.4331 16 11.9442 16H3.05534ZM11.9442 2.66667H3.05534V14.2222H11.9442V2.66667ZM4.83312 12.4444H6.61089V4.44444H4.83312V12.4444ZM8.38867 12.4444H10.1664V4.44444H8.38867V12.4444Z"
                fill="#0476E2 "
              />
            </svg>
          ),
        },
      ],
      Delisted: [
        {
          status: "DELISTED_BY_SELLER",
          text: "By Seller",
          count: groupAppsCounts.DELISTED_BY_SELLER,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "DELISTED_BY_ADMIN",
          text: "By Admin",
          count: groupAppsCounts.DELISTED_BY_ADMIN,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "DELISTED_BY_APPROVER",
          text: "By Approver",
          count: groupAppsCounts.DELISTED_BY_APPROVER,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
      ],
    },
    "3rd Party Apps": {
      Production: [
        {
          status: "DOMAIN_APPROVAL_IN_REVIEW",
          text: "In Review",
          count: thirdPartyCounts.DOMAIN_APPROVAL_IN_REVIEW,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "DOMAIN_APPROVAL_APPROVED",
          text: "Approved",
          count: thirdPartyCounts.DOMAIN_APPROVAL_APPROVED,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.90962 16L4.462 13.5619L1.71914 12.9524L1.98581 10.1333L0.119141 8L1.98581 5.86667L1.71914 3.04762L4.462 2.4381L5.90962 0L8.50009 1.10476L11.0906 0L12.5382 2.4381L15.281 3.04762L15.0144 5.86667L16.881 8L15.0144 10.1333L15.281 12.9524L12.5382 13.5619L11.0906 16L8.50009 14.8952L5.90962 16ZM7.70009 10.7048L12.0049 6.4L10.9382 5.29524L7.70009 8.53333L6.062 6.93333L4.99533 8L7.70009 10.7048Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
        {
          status: "REJECTED_BY_DOMAIN_APPROVER",
          text: "Rejected",
          count: thirdPartyCounts.REJECTED_BY_DOMAIN_APPROVER,
          isCountNeeded: true,
          icon: (
            <svg
              width="13"
              height="16"
              viewBox="0 0 13 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.7001 16C1.2601 16 0.883431 15.8433 0.570098 15.53C0.256764 15.2167 0.100098 14.84 0.100098 14.4V1.6C0.100098 1.16 0.256764 0.783333 0.570098 0.47C0.883431 0.156667 1.2601 0 1.7001 0H8.9001L12.9001 4.8V14.12L9.2601 10.4C9.40676 10.1733 9.51676 9.92667 9.5901 9.66C9.66343 9.39333 9.7001 9.10667 9.7001 8.8C9.7001 7.92 9.38676 7.16667 8.7601 6.54C8.13343 5.91333 7.3801 5.6 6.5001 5.6C5.6201 5.6 4.86676 5.91333 4.2401 6.54C3.61343 7.16667 3.3001 7.92 3.3001 8.8C3.3001 9.68 3.61343 10.4333 4.2401 11.06C4.86676 11.6867 5.6201 12 6.5001 12C6.80676 12 7.09343 11.9633 7.3601 11.89C7.62676 11.8167 7.87343 11.7067 8.1001 11.56L12.2601 15.68C11.9534 15.8133 11.6334 15.9 11.3001 15.94C10.9668 15.98 10.6334 16 10.3001 16H1.7001ZM6.5001 10.4C6.0601 10.4 5.68343 10.2433 5.3701 9.93C5.05676 9.61667 4.9001 9.24 4.9001 8.8C4.9001 8.36 5.05676 7.98333 5.3701 7.67C5.68343 7.35667 6.0601 7.2 6.5001 7.2C6.9401 7.2 7.31676 7.35667 7.6301 7.67C7.94343 7.98333 8.1001 8.36 8.1001 8.8C8.1001 9.24 7.94343 9.61667 7.6301 9.93C7.31676 10.2433 6.9401 10.4 6.5001 10.4Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          ),
        },
      ],
    },
  };

  const columns = [
    {
      field: "appIcon",
      headerName: "",
      width: 10,
      sortable: false,
      renderCell: (params) => {
        const { row } = params;

        if (row.isChild) {
          return null;
        }

        const iconUrl = row.iconUrl || row.currentSubmission?.iconUrl;

        return iconUrl ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <img
              src={iconUrl}
              alt={row.applicationName || "App Icon"}
              width="30"
              height="30"
              style={{
                borderRadius: "4px",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>
        ) : null;
      },
    },
    {
      field: "applicationName",
      headerName: "Application Name",
      flex: 1,
      minWidth: 230,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => (
        <Box sx={{ textAlign: "center" }}>Application Name</Box>
      ),
      renderCell: renderAppNameCell,
    },
    // Update the version column in the columns array (around line 950):
    {
      field: "version",
      headerName: "Version",
      sortable: true,
      maxWidth: 1000,
      minWidth: 160,
      flex: 1,
      renderCell: (params) => {
        const { row } = params;
        const columnWidth = params.colDef.computedWidth;

        let fullVersion = "";
        if (selectedTab === 0) {
          // Group Apps - use submissionVersion or version
          fullVersion = row.submissionVersion || row.version || "";
        } else {
          // 3rd Party Apps - use params.value
          fullVersion = params.value || "";
        }

        const shouldTruncate =
          (columnWidth < 160 && fullVersion.length > 9) ||
          (columnWidth >= 160 && fullVersion.length > 17);

        let displayVersion;
        if (!shouldTruncate) {
          displayVersion = fullVersion;
        } else if (columnWidth < 160) {
          displayVersion = `${fullVersion.substring(0, 9)}...`;
        } else {
          displayVersion = `${fullVersion.substring(0, 17)}...`;
        }

        if (row.isGroup) {
          return (
            <div className="versionIcon">
              <Tooltip
                title={shouldTruncate ? fullVersion : ""}
                placement="top"
                arrow
                disableInteractive
                disableHoverListener={!shouldTruncate}
              >
                <Typography
                  sx={{
                    marginLeft: "5px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {displayVersion}
                </Typography>
              </Tooltip>
              {/* Show expand/collapse icon for both Group Apps and 3rd Party Apps */}
              {selectedTab === 0 && row.totalSubmissions > 1 && (
                <div style={{ marginRight: "5px" }}>
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "5px" }}
                  >
                    <path
                      d="M9.96732 8.06366L8.76079 7.37995V6.01255L9.96732 5.32885L11.1739 6.01255V7.37995L9.96732 8.06366ZM9.96732 9.91368L12.7826 8.30497V5.08754L9.96732 3.47882L7.15207 5.08754V8.30497L9.96732 9.91368ZM8.51948 14.3376H13.0239C12.93 14.6862 12.7692 14.9677 12.5413 15.1822C12.3134 15.3967 12.0184 15.5308 11.6565 15.5844L2.88898 16.6502C2.44659 16.7172 2.04776 16.6133 1.6925 16.3385C1.33724 16.0637 1.1328 15.7051 1.07918 15.2627L0.0134059 6.47505C-0.0402179 6.03266 0.0670297 5.63718 0.335149 5.28863C0.603268 4.94007 0.958525 4.73898 1.40092 4.68536L2.32593 4.56471V6.17342L1.60201 6.27396L2.68789 15.0616L8.51948 14.3376ZM5.54336 12.7289C5.10096 12.7289 4.72225 12.5714 4.40721 12.2564C4.09217 11.9413 3.93465 11.5626 3.93465 11.1202V2.27229C3.93465 1.82989 4.09217 1.45117 4.40721 1.13613C4.72225 0.821094 5.10096 0.663574 5.54336 0.663574H14.3913C14.8337 0.663574 15.2124 0.821094 15.5274 1.13613C15.8425 1.45117 16 1.82989 16 2.27229V11.1202C16 11.5626 15.8425 11.9413 15.5274 12.2564C15.2124 12.5714 14.8337 12.7289 14.3913 12.7289H5.54336ZM5.54336 11.1202H14.3913V2.27229H5.54336V11.1202Z"
                      fill="#121415"
                      fillOpacity="0.6"
                    />
                  </svg>
                </div>
              )}
              {selectedTab === 1 && row.childCount > 0 && (
                <div style={{ marginRight: "5px" }}>
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "5px" }}
                  >
                    <path
                      d="M9.96732 8.06366L8.76079 7.37995V6.01255L9.96732 5.32885L11.1739 6.01255V7.37995L9.96732 8.06366ZM9.96732 9.91368L12.7826 8.30497V5.08754L9.96732 3.47882L7.15207 5.08754V8.30497L9.96732 9.91368ZM8.51948 14.3376H13.0239C12.93 14.6862 12.7692 14.9677 12.5413 15.1822C12.3134 15.3967 12.0184 15.5308 11.6565 15.5844L2.88898 16.6502C2.44659 16.7172 2.04776 16.6133 1.6925 16.3385C1.33724 16.0637 1.1328 15.7051 1.07918 15.2627L0.0134059 6.47505C-0.0402179 6.03266 0.0670297 5.63718 0.335149 5.28863C0.603268 4.94007 0.958525 4.73898 1.40092 4.68536L2.32593 4.56471V6.17342L1.60201 6.27396L2.68789 15.0616L8.51948 14.3376ZM5.54336 12.7289C5.10096 12.7289 4.72225 12.5714 4.40721 12.2564C4.09217 11.9413 3.93465 11.5626 3.93465 11.1202V2.27229C3.93465 1.82989 4.09217 1.45117 4.40721 1.13613C4.72225 0.821094 5.10096 0.663574 5.54336 0.663574H14.3913C14.8337 0.663574 15.2124 0.821094 15.5274 1.13613C15.8425 1.45117 16 1.82989 16 2.27229V11.1202C16 11.5626 15.8425 11.9413 15.5274 12.2564C15.2124 12.5714 14.8337 12.7289 14.3913 12.7289H5.54336ZM5.54336 11.1202H14.3913V2.27229H5.54336V11.1202Z"
                      fill="#121415"
                      fillOpacity="0.6"
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        } else if (row.isChild) {
          return (
            <Box sx={{ marginLeft: "25px" }}>
              <Tooltip
                title={shouldTruncate ? fullVersion : ""}
                placement="top"
                arrow
                disableInteractive
                disableHoverListener={!shouldTruncate}
              >
                <Typography
                  sx={{ marginLeft: "5px", fontSize: "14px", color: "#5A5B5C" }}
                >
                  {displayVersion}
                </Typography>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box>
            <Tooltip
              title={shouldTruncate ? fullVersion : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncate}
            >
              <Typography sx={{ marginLeft: "5px", fontSize: "14px" }}>
                {displayVersion}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },

    {
      field: "publisherId",
      headerName: "Publisher",
      flex: 1,
      minWidth: 100,
      maxWidth: 1000,
      sortable: true,
      renderCell: (params) => {
        const { row } = params;
        let publisherId = row.publisherId;

        let submissionsArray;
        if (Array.isArray(row.submissions)) {
          submissionsArray = row.submissions;
        } else if (row.submissions) {
          submissionsArray = [row.submissions];
        } else {
          submissionsArray = [];
        }
        if (!publisherId && submissionsArray && submissionsArray.length > 0) {
          const firstSubmission = submissionsArray[0];
          publisherId =
            firstSubmission.publisherId ||
            firstSubmission.sellerId ||
            firstSubmission.ownerId ||
            firstSubmission.createdBy;
        }

        const displayName = getPublisherName(publisherId) || "";

        const columnWidth = params.colDef.computedWidth;
        let truncatedName;
        if (columnWidth < 120 && displayName.length > 7) {
          truncatedName = `${displayName.substring(0, 7)}...`;
        } else if (columnWidth > 120 && displayName.length > 15) {
          truncatedName = `${displayName.substring(0, 15)}...`;
        } else {
          truncatedName = displayName;
        }

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                textDecoration: "underline",
                fontSize: "14px",
                cursor: "pointer",
                marginLeft: "5px",
              }}
              title={`${displayName} (${publisherId || "N/A"})`}
            >
              {truncatedName}
            </Typography>
          );
        }

        return (
          <Box>
            <Typography
              sx={{
                textDecoration: "underline",
                fontSize: "14px",
                cursor: "pointer",
                marginLeft: "5px",
              }}
              title={`${displayName} (${publisherId || "N/A"})`}
            >
              {truncatedName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "appStatus",
      headerName: "Status",
      flex: 1,
      minWidth: 135,
      maxWidth: 1000,
      sortable: true,
      renderCell: (params) => {
        let status;
        if (selectedTab === 0) {
          status = activeStatus;
        } else {
          status = activeThirdPartyStatus;
        }

        // Define status badge styles
        const getStatusBadge = (status) => {
          const statusConfig = {
            DRAFT: {
              className: styles.draftStatusBadge,
              text: "Draft",
            },
            PUBLISHED: {
              className: styles.publishedStatusBadge,
              text: "Published",
            },
            REJECTED_FOR_PRODUCTION: {
              className: styles.delistedStatusBadge,
              text: "Rejected",
            },
            APPROVED_FOR_PRODUCTION: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
            APPROVED_FOR_BETA: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
            APPROVED_FOR_STAGING: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
            IN_REVIEW: {
              className: styles.inReviewStatusBadge,
              text: "In Review",
            },
            GENERATING_DELTA: {
              className: styles.inReviewStatusBadge,
              text: "Generating Delta",
            },
            DELISTED_BY_ADMIN: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Admin",
            },
            DELISTED_BY_SELLER: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Seller",
            },
            DELISTED_BY_APPROVER: {
              className: styles.delistedStatusBadge,
              text: "Delisted by Approver",
            },
            SUBMITTED_FOR_PRODUCTION: {
              className: styles.submittedStatusBadge,
              text: "Submitted",
            },
            SUBMITTED_FOR_STAGING: {
              className: styles.submittedStatusBadge,
              text: "Submitted",
            },
            SUBMITTED_FOR_BETA: {
              className: styles.submittedStatusBadge,
              text: "Submitted",
            },
            DELETED: {
              className: styles.delistedStatusBadge,
              text: "Deleted",
            },
            // 3rd Party App statuses
            DOMAIN_APPROVAL_IN_REVIEW: {
              className: styles.inReviewStatusBadge,
              text: "In Review",
            },
            DOMAIN_APPROVAL_APPROVED: {
              className: styles.submittedStatusBadge,
              text: "Approved",
            },
            REJECTED_BY_DOMAIN_APPROVER: {
              className: styles.delistedStatusBadge,
              text: "Rejected",
            },
          };

          return (
            statusConfig[status] || {
              className: styles.defaultStatusBadge,
              text: status || "Unknown",
            }
          );
        };

        const statusBadge = getStatusBadge(status);
        const columnWidth = params.colDef.computedWidth;

        const maxChars = Math.floor(columnWidth / 7.5);
        const shouldTruncate = statusBadge.text.length > maxChars;
        const displayText = shouldTruncate
          ? `${statusBadge.text.substring(0, maxChars - 3)}...`
          : statusBadge.text;

        return (
          <Box sx={{ marginLeft: "5px" }}>
            <Tooltip
              title={shouldTruncate ? statusBadge.text : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncate}
            >
              <span className={statusBadge.className}>{displayText}</span>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "submittedAt",
      headerName: "Published",
      flex: 1,
      minWidth: 130,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => <Box sx={{ textAlign: "center" }}>Published</Box>,
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          const submittedAt =
            row.currentSubmission?.submittedAt ||
            row.submittedAt ||
            row.lastModified;

          if (!submittedAt) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  // paddingLeft: "8px",
                }}
                onClick={($event) =>
                  row.isThirdParty
                    ? handleThirdPartyGroupToggle($event, row.id)
                    : handleGroupToggle($event, row.id)
                }
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    // fontWeight: "600",
                    marginLeft: "5px",
                  }}
                >
                  N/A
                </Typography>
                {row.isExpanded ? (
                  <KeyboardArrowUp
                    sx={{ fontSize: "16px", marginLeft: "8px" }}
                  />
                ) : (
                  <KeyboardArrowDown
                    sx={{ fontSize: "16px", marginLeft: "8px" }}
                  />
                )}
              </Box>
            );
          }

          const dateObj = new Date(submittedAt);
          if (Number.isNaN(dateObj)) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={($event) =>
                  row.isThirdParty
                    ? handleThirdPartyGroupToggle($event, row.id)
                    : handleGroupToggle($event, row.id)
                }
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    // fontWeight: "600",
                    marginLeft: "5px",
                  }}
                >
                  Invalid Date
                </Typography>
                {row.isExpanded ? (
                  <IconButton
                    sx={{
                      padding: "4px",
                      borderRadius: "4px",
                      marginRight: "4px",
                    }}
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                ) : (
                  <IconButton
                    sx={{
                      padding: "4px",
                      borderRadius: "4px",
                      marginRight: "4px",
                    }}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                )}
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%",
                justifyContent: "space-between",
                // paddingLeft: "8px",
              }}
              onClick={($event) =>
                row.isThirdParty
                  ? handleThirdPartyGroupToggle($event, row.id)
                  : handleGroupToggle($event, row.id)
              }
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  // fontWeight: "600",
                  marginLeft: "5px",
                }}
              >
                {dateObj.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </Typography>
              {row.isExpanded ? (
                <IconButton
                  sx={{
                    padding: "4px",
                    borderRadius: "4px",
                    marginRight: "4px",
                  }}
                >
                  <KeyboardArrowUp />
                </IconButton>
              ) : (
                <IconButton
                  sx={{
                    padding: "4px",
                    borderRadius: "4px",
                    marginRight: "4px",
                  }}
                >
                  <KeyboardArrowDown />
                </IconButton>
              )}
            </Box>
          );
        }

        const submittedAt =
          row.currentSubmission?.submittedAt ||
          row.submittedAt ||
          row.lastModified;

        if (!submittedAt) {
          return (
            <Box>
              <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
                N/A
              </Typography>
            </Box>
          );
        }

        const dateObj = new Date(submittedAt);
        if (Number.isNaN(dateObj)) {
          return (
            <Box>
              <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
                Invalid Date
              </Typography>
            </Box>
          );
        }

        return (
          <Box>
            <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
              {dateObj.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const deletedColumns = [
    {
      field: "applicationName",
      headerName: "Application Name",
      flex: 1,
      minWidth: 230,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", marginLeft: "8px" }}>
          Application Name
        </Box>
      ),
      renderCell: (params) => {
        const columnWidth = params.colDef.computedWidth;
        const title = params.row.applicationName || "Untitled";

        const shouldTruncateTitle =
          (columnWidth < 175 && title.length > 17) ||
          (columnWidth >= 175 && title.length > 25);

        let titleDisplay;
        if (!shouldTruncateTitle) {
          titleDisplay = title;
        } else if (columnWidth < 175) {
          titleDisplay = `${title.slice(0, 17)}...`;
        } else {
          titleDisplay = `${title.slice(0, 25)}...`;
        }

        return (
          <Box
            sx={{
              display: "flex",
              gap: "2px",
              flexDirection: "column",
            }}
          >
            <Tooltip
              title={shouldTruncateTitle ? title : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncateTitle}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  cursor: "pointer",
                  fontWeight: "600",
                  marginLeft: "13px",
                  color: "#2A2C2D",
                }}
              >
                {titleDisplay}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 100,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => <Box sx={{ textAlign: "center" }}>Version</Box>,
      renderCell: (params) => {
        const columnWidth = params.colDef.computedWidth;
        const fullVersion = params.value || "";
        const shouldTruncate =
          (columnWidth < 160 && fullVersion.length > 9) ||
          (columnWidth >= 160 && fullVersion.length > 17);

        let displayVersion;
        if (!shouldTruncate) {
          displayVersion = fullVersion || "N/A";
        } else if (columnWidth < 160) {
          displayVersion = `${fullVersion.substring(0, 9)}...`;
        } else {
          displayVersion = `${fullVersion.substring(0, 17)}...`;
        }

        return (
          <Box>
            <Tooltip
              title={shouldTruncate ? fullVersion : ""}
              placement="top"
              arrow
              disableInteractive
              disableHoverListener={!shouldTruncate}
            >
              <Typography sx={{ marginLeft: "5px", fontSize: "14px" }}>
                {displayVersion}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "publisherId",
      headerName: "Publisher",
      flex: 1,
      minWidth: 100,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => <Box sx={{ textAlign: "center" }}>Publisher</Box>,
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                marginLeft: "5px",
              }}
            >
              -
            </Typography>
          );
        }

        // Try to get publisher ID from different sources for deleted applications
        const publisherId = row.publisherId || row.sellerId || row.createdBy;
        const displayName = getPublisherName(publisherId);

        const columnWidth = params.colDef.computedWidth;
        let truncatedName;
        if (columnWidth < 120 && displayName.length > 7) {
          truncatedName = `${displayName.substring(0, 7)}...`;
        } else if (columnWidth > 120 && displayName.length > 15) {
          truncatedName = `${displayName.substring(0, 15)}...`;
        } else {
          truncatedName = displayName;
        }

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Typography
              sx={{
                textDecoration: "underline",
                fontSize: "14px",
                cursor: "pointer",
                marginLeft: "5px",
              }}
              title={`${displayName} (${publisherId || "N/A"})`}
            >
              {truncatedName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "deletedAt",
      headerName: "Deleted",
      flex: 1,
      minWidth: 80,
      maxWidth: 1000,
      sortable: true,
      renderHeader: () => <Box sx={{ textAlign: "center" }}>Deleted</Box>,
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                marginLeft: "5px",
              }}
            >
              -
            </Typography>
          );
        }

        const deletedAt = row.deletedAt || row.lastModified || row.submittedAt;
        if (!deletedAt) {
          return (
            <Box>
              <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
                N/A
              </Typography>
            </Box>
          );
        }

        const dateObj = new Date(deletedAt);
        if (Number.isNaN(dateObj)) {
          return (
            <Box>
              <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
                Invalid Date
              </Typography>
            </Box>
          );
        }

        return (
          <Box>
            <Typography sx={{ fontSize: "14px", marginLeft: "5px" }}>
              {dateObj.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      minWidth: 240,
      maxWidth: 1000,
      sortable: false,
      renderHeader: () => <Box sx={{ textAlign: "center" }}></Box>,
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                marginLeft: "5px",
              }}
            >
              -
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              marginLeft: "5px",
            }}
          >
            <Button
              size="small"
              disabled={rejectLoading}
              sx={{
                fontSize: "12px",
                padding: "6px 12px",
                minWidth: "120px",
                textTransform: "none",
                color: "#2A2C2D",
                backgroundColor: "#1214151A",
                border: "1px solid #1214151A",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#12141533",
                },
              }}
              onClick={() =>
                handleRejectClick(
                  row.id || row.applicationId,
                  row.applicationName
                )
              }
            >
              {rejectLoading ? "..." : "Cancel Deletion"}
            </Button>
            <Button
              size="small"
              sx={{
                fontSize: "12px",
                padding: "6px 12px",
                minWidth: "50px",
                textTransform: "none",
                color: "#B50B00",
                backgroundColor: "transparent",
                border: "1px solid #B50B00",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                "&:hover": {
                  backgroundColor: "#B50B0010",
                },
              }}
              onClick={() =>
                handleDeleteClick(
                  row.id || row.applicationId,
                  row.applicationName
                )
              }
            >
              <svg
                width="15"
                height="17"
                viewBox="0 0 15 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.05534 16.6667C2.56645 16.6667 2.14793 16.4927 1.79978 16.1445C1.45163 15.7964 1.27756 15.3779 1.27756 14.889V3.33341H0.388672V1.55564H4.83312V0.666748H10.1664V1.55564H14.6109V3.33341H13.722V14.889C13.722 15.3779 13.5479 15.7964 13.1998 16.1445C12.8516 16.4927 12.4331 16.6667 11.9442 16.6667H3.05534ZM11.9442 3.33341H3.05534V14.889H11.9442V3.33341ZM4.83312 13.1112H6.61089V5.11119H4.83312V13.1112ZM8.38867 13.1112H10.1664V5.11119H8.38867V13.1112Z"
                  fill="#B50B00"
                />
              </svg>
            </Button>
          </Box>
        );
      },
    },
  ];

  // Helper function to format status for display
  const formatStatusForDisplay = (status) => {
    const statusMap = {
      // Group Apps statuses
      SUBMITTED_FOR_BETA: "Submitted for Beta",
      APPROVED_FOR_BETA: "Approved for Beta",
      SUBMITTED_FOR_STAGING: "Submitted for Staging",
      APPROVED_FOR_STAGING: "Approved for Staging",
      IN_REVIEW: "In Review",
      SUBMITTED_FOR_PRODUCTION: "Submitted for Production",
      APPROVED_FOR_PRODUCTION: "Approved for Production",
      PUBLISHED: "Published",
      GENERATING_DELTA: "Generating Delta",
      DELETED: "Deleted Applications",
      DELISTED_BY_SELLER: "Delisted by Seller",
      DELISTED_BY_ADMIN: "Delisted by Admin",
      DELISTED_BY_APPROVER: "Delisted by Approver",
      // 3rd Party Apps statuses
      DOMAIN_APPROVAL_IN_REVIEW: "In Review",
      DOMAIN_APPROVAL_APPROVED: "Approved for Production",
      REJECTED_BY_DOMAIN_APPROVER: "Rejected",
    };
    return statusMap[status] || status;
  };

  const toggleSection = ($event, section) => {
    $event.stopPropagation();

    setOpenSections((prev) => {
      const newOpenSections = {
        ...prev,
        [section]: !prev[section],
      };
      sessionStorage.setItem(
        "domain-approver-openSections",
        JSON.stringify(newOpenSections)
      );
      return newOpenSections;
    });
  };

  // Filter applications based on search term
  const getFilteredApplications = () => {
    let dataSource;

    if (selectedTab === 0) {
      // Group Apps tab
      if (activeStatus === "DELETED") {
        dataSource = deletedApplications || [];
      } else {
        // Use filtered application list instead of full list
        dataSource =
          filteredApplicationList.length > 0 ||
            appliedFilters.selectedCountries.length > 0 ||
            appliedFilters.selectedEcus.length > 0
            ? filteredApplicationList
            : applicationList;
      }
    } else {
      // 3rd Party Apps tab - data is already grouped by processThirdPartyData
      dataSource = thirdPartyApplications;
    }

    // Apply search filter first
    let filteredData = dataSource;
    if (searchTerm.trim()) {
      filteredData = dataSource.filter((app) => {
        const appName = app.applicationName || "";
        const publisherId = app.publisherId || "";
        const searchLower = searchTerm.toLowerCase();

        if (selectedTab === 0) {
          // GROUP APPS search - also search within submissions
          return (
            appName.toLowerCase().includes(searchLower) ||
            publisherId.toLowerCase().includes(searchLower) ||
            (app.submissions?.some((submission) =>
                submission.name?.toLowerCase().includes(searchLower)
              ))
          );
        } else {
          // 3RD PARTY APPS search
          return (
            appName.toLowerCase().includes(searchLower) ||
            publisherId.toLowerCase().includes(searchLower)
          );
        }
      });
    }

    // Apply grouping based on tab
    if (selectedTab === 0 && activeStatus !== "DELETED") {
      // GROUP APPS - group by applicationName with submissions
      return groupApplicationsByApplicationName(filteredData);
    } else if (selectedTab === 1) {
      // 3RD PARTY APPS - data is already grouped, ensure all have id
      return filteredData.map(app => ({
        ...app,
        id: app.id || app.applicationId || `thirdparty_${app.applicationId || getSecureRandomId()}`,
      }));
    } else {
      // DELETED tab or other cases - no grouping, ensure all have id
      return filteredData.map(app => ({
        ...app,
        id: app.id || app.applicationId || `deleted_${app.applicationId || getSecureRandomId()}`,
      }));
    }
  };

  // Handle group expand/collapse
  const handleGroupToggle = ($event, groupId) => {
    $event.stopPropagation();

    if (selectedTab === 0) {
      // GROUP APPS - use expandedGroups
      setExpandedGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }
        return newSet;
      });
    } else {
      // 3RD PARTY APPS - use expandedThirdPartyGroups
      setExpandedThirdPartyGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }
        return newSet;
      });
    }
  };

  // Handle third party group expand/collapse
  const handleThirdPartyGroupToggle = ($event, groupId) => {
    $event.stopPropagation();
    setExpandedThirdPartyGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getPaginationCount = () => {
    if (searchTerm.trim()) {
      return getFilteredApplications().filter((row) => !row.isChild).length;
    }
    if (selectedTab === 0) {
      return groupAppsCounts[activeStatus] || 0;
    }
    // For 3rd party apps, count the grouped items (excluding child rows)
    return thirdPartyApplications.filter((row) => !row.isChild).length;
  };

  // Filter-related functions

  const handleFilterApiCalls = useCallback(
    (data) => {
      // Store applied filters
      setAppliedFilters(data);

      // Apply client-side filtering to applicationList
      const filteredApps = applyFiltersToApplications(applicationList, data);
      setFilteredApplicationList(filteredApps);
    },
    [applicationList]
  );

  // Function to apply filters to application list (memoized to prevent excessive re-computations)
  const applyFiltersToApplications = useCallback((applications, filters) => {
    if (!applications || applications.length === 0) {
      return [];
    }

    return applications.filter((app) => {
      if (filters.selectedCountries && filters.selectedCountries.length > 0) {
        if (!filters.selectedCountries.some((c) => appMatchesCountry(app, c))) return false;
      }

      if (filters.selectedEcus && filters.selectedEcus.length > 0) {
        const appEcus = getAppEcus(app);
        if (appEcus.length === 0) return true;
        if (!filters.selectedEcus.some((e) => appMatchesEcu(app, e))) return false;
      }

      return true;
    });
  }, []); // No dependencies needed since it's a pure function

  // Function to clear all filters (memoized to prevent unnecessary re-renders)
  const clearAllFilters = useCallback(() => {
    setAppliedFilters({
      selectedCountries: [],
      selectedEcus: [],
    });
    setFilteredApplicationList(applicationList);
  }, [applicationList]);

  // Get appropriate columns based on current state
  const getCurrentColumns = () => {
    if (selectedTab === 0 && activeStatus === "DELETED") {
      return deletedColumns;
    }
    return columns;
  };

  // Memoize the DrawerFilter component to prevent unnecessary re-renders
  const memoizedDrawerFilter = useMemo(
    () => (
      <DrawerFilter
        openFilter={openFilter}
        toggleDrawer={toggleDrawer}
        selectedCountries={memoizedCountries}
        selectedEcus={memoizedEcu}
        handleFilterApiCalls={handleFilterApiCalls}
      />
    ),
    [
      openFilter,
      toggleDrawer,
      memoizedCountries,
      memoizedEcu,
      handleFilterApiCalls,
    ]
  );

  // Add the 401 error component before the main return statement
  if (show401Error) {
    return (
      <>
        <Topheader />
        <div
          className="noReadyLinkMarketPlace"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "100vw",
            minHeight: "60vh",
            textAlign: "center",
            padding: "20px",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <svg
              width="300"
              height="25"
              viewBox="0 0 300 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.4269 19.7366L5.16686 14.7584H2.73925V19.7366H-7.62939e-06V5.09715H5.03973C8.32438 5.09715 10.3911 7.10239 10.3911 10.0098C10.3911 12.0396 9.38646 13.5446 7.67238 14.2745L11.3302 19.7325H8.4228L8.4269 19.7366ZM2.73925 12.3964H4.89211C6.54469 12.3964 7.65187 11.6869 7.65187 10.0139C7.65187 8.34079 6.54469 7.63137 4.89211 7.63137H2.73925V12.4005V12.3964ZM22.3323 17.2065V19.7366H13.2165V5.09715H22.1437V7.62727H15.9558V11.1416H20.6592V13.6102H15.9558V17.2065H22.3323ZM34.212 16.9153H27.3311L26.1788 19.7366H23.2714L29.4183 5.09715H32.1166L38.284 19.7366H35.3561L34.2038 16.9153H34.212ZM33.2894 14.6558L30.7797 8.46792L28.2496 14.6558H33.2894ZM52.3862 12.3554C52.3862 16.4971 49.1672 19.7366 44.9845 19.7366H39.9653V5.09715H44.9845C49.1672 5.09715 52.3862 8.33669 52.3862 12.3513V12.3554ZM49.6019 12.3554C49.6019 9.84575 47.9288 7.69288 44.9599 7.69288H42.7004V17.145H44.9599C47.9288 17.145 49.6019 14.9921 49.6019 12.3554ZM60.7721 13.7783V19.7366H58.0329V13.7783L52.4929 5.10125H55.5889L59.3943 11.2072L63.1998 5.10125H66.3163L60.7762 13.7783H60.7721Z"
                fill="#003865"
              />
              <path
                d="M115.358 12.2897C115.358 13.7086 114.206 14.8609 112.787 14.8609C111.368 14.8609 110.216 13.7086 110.216 12.2897C110.216 10.8709 111.368 9.7186 112.787 9.7186C114.206 9.7186 115.358 10.8709 115.358 12.2897ZM84.0164 10.8709C84.1394 8.42279 86.6286 7.14337 87.9285 7.14337C89.6467 7.14337 91.4797 8.24646 91.4797 11.3466V19.6095H94.2025V11.1046C94.2025 6.37245 91.5289 4.69937 89.2366 4.69937C86.4112 4.69937 84.205 6.97525 84.0164 7.57805V4.97002H81.2936V19.6095H84.0164V10.8709ZM69.9675 0.00408317V19.6136H72.6821V0.00408317H69.9675ZM75.6387 4.97411V19.6136H78.3534V4.97411H75.6387ZM75.6387 0.00408317V2.75564H78.3534V0.00408317H75.6387ZM103.003 11.5639L108.875 4.97002H105.594L99.7917 11.5803V-1.52588e-05H97.1591V19.6095H99.7917V15.1889L101.301 13.483L106.833 19.6095H110.052L103.003 11.5639ZM226.999 0.00408317V19.6136H229.714V0.00408317H226.999ZM178.693 11.5639L184.565 4.97002H181.285L175.482 11.5803V-1.52588e-05H172.85V19.6095H175.482V15.1889L176.991 13.483L182523 19.6095H185.742L178.693 11.5639ZM255.154 17.4033C252.956 17.4033 250.623 15.6195 250.623 12.3184C250.623 9.01739 252.961 7.17208 255.154 7.17208C256.93 7.17208 257.926 7.9102 258.689 8.77135L260.694 7.76668C259.456 5.76145 257.541 4.70347 255.13 4.70347C252.173 4.70347 248.007 7.06546 248.007 12.3225C248.007 17.5796 252.173 19.8801 255.13 19.8801C257.537 19.8801 259.468 18.8344 260.744 16.8456L258.697 15.8368C257.943 16.6816 256.959 17.4074 255.163 17.4074L255.154 17.4033ZM164.587 8.40639V4.97411H161.839V19.6136H164.587V13.1058C164.886 9.78422 167.211 7.46323 170.242 7.46323C170.377 7.46323 170.533 7.46323 170.701 7.47143V4.71577C170.524 4.70347 170.34 4.70347 170.127 4.70347C167.859 4.70347 165.206 6.04439 164.583 8.40639H164.587ZM204.519 0.963645H201.78V4.97002H199.725V7.23769H201.78V16.2715C201.78 18.8303 202.813 19.876 205.343 19.876C206.426 19.876 207.193 19.6382 207.644 19.4372V17.186C207.254 17.2885 206.832 17.3664 206.356 17.3664C205.138 17.3664 204.519 16.7144 204.519 15.4309V7.24179H207.644V4.97411H204.519V0.967743V0.963645ZM138.076 4.70347C137.309 4.70347 136.058 4.888 134.771 5.71634C133.524 6.52007 132.228 7.96761 131.999 8.24646H131.826C130.986 5.67943 129.009 4.70347 127.258 4.70347C124.42 4.70347 122.202 6.97935 122.017 7.58215V4.97411H119.282V19.6136H122.017V10.8791C122.14 8.43099 124.642 7.15158 125.946 7.15158C127.668 7.15158 129.509 8.25466 129.509 11.3548V19.6177H132.835V10.8832C132.958 8.43509 135.46 7.15568 136.764 7.15568C138.486 7.15568 140.327 8.25876 140.327 11.3589V19.6218H143.062V11.121C143.062 6.38885 140.376 4.71577 138.076 4.71577V4.70347ZM224.793 12.2938C224.793 17.2229 221.324 19.876 218.06 19.876C215.739 19.876 213.426 18.4613 212.983 17.6698V24.8337H210.268V4.97002H212.983V7.03266L213.02 6.95884C213.397 6.17971 215.698 4.69937 218.064 4.69937C219.696 4.69937 221.336 5.38418 222.57 6.56518C223.587 7.54114 224.797 9.32494 224.797 12.2938H224.793ZM222.164 12.2938C222.164 9.75962 220.582 7.19259 217.559 7.19259C215.242 7.19259 212.901 8.94358 212.901 12.2938C212.901 15.6441 215.247 17.3951 217.559 17.3951C220.582 17.3951 222.164 14.8281 222.164 12.2938ZM198.717 13.2247H187.719L187.838 13.7906C188.33 16.1485 190.064 17.555 192.471 17.555C194.267 17.555 195.198 17.1203 196.178 16.4847L197.601 17.961C196.301 19.265 194.624 19.876 192.365 19.876C190.56 19.876 188.768 19.1871 187.452 17.9897C186.394 17.0219 185.131 15.2545 185.131 12.3184C185.131 7.06546 189.215 4.69937 192.115 4.69937C193.595 4.69937 195.694 5.32677 197.146 7.09417C198.401 8.61962 198.946 10.7356 198.725 13.2247H198.717ZM196.408 11.3507L196.33 10.8135C195.949 8.23006 193.874 7.05316 192.11 7.05316C190.347 7.05316 188.444 8.20546 187.87 10.7807L187.743 11.3548H196.412L196.408 11.3507ZM275.576 13.2247H264.578L264.697 13.7906C265.189 16.1485 266.923 17.555 269.33 17.555C271.127 17.555 272.057 17.1203 273.037 16.4847L274.46 17.961C273.16 19.265 271.483 19.876 269.224 19.876C267.42 19.876 265.628 19.1871 264.311 17.9897C263.253 17.0219 261.99 15.2545 261.99 12.3184C261.99 7.06546 266.075 4.69937 268.974 4.69937C270.454 4.69937 272.554 5.32677 274.005 7.09417C275.26 8.61962 275.805 10.7356 275.584 13.2247H275.576ZM273.263 11.3507L273.185 10.8135C272.804 8.23006 270.729 7.05316 268.966 7.05316C267.202 7.05316 265.299 8.20546 264.725 10.7807L264.598 11.3548H273.267L273.263 11.3507ZM156.717 4.97411H159.428V19.6136H156.717V17.5509L156.681 17.6247C156.303 18.4039 154.003 19.8801 151.637 19.8801C148.373 19.8801 144.903 17.2229 144.903 12.2938C144.903 9.32494 146.113 7.54114 147.13 6.56518C148.364 5.38418 150.005 4.70347 151.637 4.70347C153.958 4.70347 156.275 6.1223 156.717 6.91374V4.97411ZM156.804 12.2897C156.804 8.93948 154.458 7.18438 152.141 7.18438C149.119 7.18438 147.536 9.75141 147.536 12.2897C147.536 14.8281 149.119 17.3951 152.141 17.3951C154.458 17.3951 156.804 15.64 156.804 12.2897ZM156.717 4.97411H159.428V19.6136H156.717V17.5509L156.681 17.6247C156.303 18.4039 154.003 19.8801 151.637 19.8801C148.373 19.8801 144.903 17.2229 144.903 12.2938C144.903 9.32494 146.113 7.54114 147.13 6.56518C148.364 5.38418 150.005 4.70347 151.637 4.70347C153.958 4.70347 156.275 6.1223 156.717 6.91374V4.97411ZM156.804 12.2897C156.804 8.93948 154.458 7.18438 152.141 7.18438C149.119 7.18438 147.536 9.75141 147.536 12.2897C147.536 14.8281 149.119 17.3951 152.141 17.3951C154.458 17.3951 156.804 15.64 156.804 12.2897ZM243.41 4.97411H246.121V19.6136H243.41V17.5509L243.373 17.6247C242.996 18.4039 240.695 19.8801 238.329 19.8801C235.065 19.8801 231.596 17.2229 231.596 12.2938C231.596 9.32494 232.806 7.54114 233.823 6.56518C235.057 5.38418 236.697 4.70347 238.329 4.70347C240.65 4.70347 242.967 6.1223 243.41 6.91374V4.97411ZM243.496 12.2897C243.496 8.93948 241.151 7.18438 238.834 7.18438C235.811 7.18438 234.229 9.75141 234.229 12.2897C234.229 14.8281 235.811 17.3951 238.834 17.3951C241.151 17.3951 243.496 15.64 243.496 12.2897Z"
                fill="#0082FF"
              />
              <path
                d="M297.15 17.0752C296.719 17.0752 296.309 17.1737 295.94 17.3459L294.169 15.5744V9.25933L295.94 7.48784C296.309 7.66007 296.715 7.75848 297.15 7.75848C298.725 7.75848 300 6.48317 300 4.90851C300 3.33384 298.725 2.05853 297.15 2.05853C295.575 2.05853 294.3 3.33384 294.3 4.90851C294.3 5.33908 294.398 5.74915 294.571 6.11821L292.799 7.88971H286.484L284.713 6.11821C284.885 5.74915 284.983 5.34318 284.983 4.90851C284.983 3.33384 283.708 2.05853 282.133 2.05853C280.559 2.05853 279.283 3.33384 279.283 4.90851C279.283 6.48317 280.559 7.75848 282.133 7.75848C282.564 7.75848 282.974 7.66007 283.343 7.48784L285.115 9.25933V15.5744L283.343 17.3459C282.974 17.1737 282.568 17.0752 282.133 17.0752C280.559 17.0752 279.283 18.3506 279.283 19.9252C279.283 21.4999 280.559 22.7752 282.133 22.7752C283.708 22.7752 284.983 21.4999 284.983 19.9252C284.983 19.4946 284.885 19.0846 284.713 18.7155L286.484 16.944H292.799L294.571 18.7155C294.398 19.0846 294.3 19.4905 294.3 19.9252C294.3 21.4999 295.575 22.7752 297.15 22.7752C298.725 22.7752 300 21.4999 300 19.9252C300 18.3506 298.725 17.0752 297.15 17.0752ZM287.054 9.82933H292.229V15.0044H287.054V9.82933Z"
                fill="url(#paint0_linear_7581_33336)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_7581_33336"
                  x1="279.279"
                  y1="12.421"
                  x2="300.004"
                  y2="12.421"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0082FF" />
                  <stop offset="0.2" stopColor="#007DFC" />
                  <stop offset="0.42" stopColor="#006FF5" />
                  <stop offset="0.67" stopColor="#0057E9" />
                  <stop offset="0.92" stopColor="#0037D9" />
                  <stop offset="1" stopColor="#002CD4" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <img
            className="noAppsImagecls"
            src={require("../../../assets/images/noapps.png")}
            alt="no apps img"
            style={{ marginBottom: "20px" }}
          />

          <div className="noAppsWelcomeTextcls">401 Error</div>
          <div className="unauthorizedLogin">Unauthorized Access</div>
          <div className="inavlidUserLoginMessage">
            You don't have permission to access this page. Please contact your
            administrator or try logging in again.
          </div>

          <Button
            style={{
              background: "#121415F2",
              textTransform: "none",
              height: "36px",
              marginTop: "12px",
            }}
            variant="contained"
            onClick={handleLogout}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: "8px" }}
            >
              <path
                d="M17 7L16.59 7.41L17.17 8H9C8.45 8 8 8.45 8 9S8.45 10 9 10H17.17L16.59 10.59L17 11L19 9L17 7ZM5 5H12C12.55 5 13 4.55 13 4S12.55 3 12 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H12C12.55 21 13 20.55 13 20S12.55 19 12 19H5V5Z"
                fill="white"
              />
            </svg>
            GO TO LOGIN
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {!loader && (
        <>
          <Toolbar />
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "240px",
                flexShrink: 0,
                paddingLeft: "75px",
                marginTop: "24px",
              }}
            >
              {/* Conditionally render tabs only if idpLogin is true */}
              {idpLogin && (
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  sx={{
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .MuiTabs-root": { minHeight: "auto" },
                    "& .MuiTab-root": {
                      minHeight: "30px",
                      height: "30px",
                      padding: "5px 16px",
                      fontSize: "12px",
                      textTransform: "none",

                      backgroundColor: "#FFFFFF",
                      fontWeight: 400,
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      minWidth: "auto",
                      marginRight: "0px",
                      borderRadius: "0px",
                      border: "1px solid #0476E2",
                      borderRight: "none",
                      "&:last-child": {
                        borderRight: "1px solid #0476E2",
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                      },
                      "&:first-of-type": {
                        borderTopLeftRadius: "4px",
                        borderBottomLeftRadius: "4px",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#0476E2",
                        color: "#FFFFFF",
                      },
                    },
                    marginBottom: "16px",
                    marginLeft: "12px",
                  }}
                >
                  <Tab label="Group Apps" />
                  <Tab label="3rd Party Apps" />
                </Tabs>
              )}

              {/* Sidebar menu - always show for Group Apps if idpLogin is false, otherwise show based on selected tab */}
              {Object.entries(
                !idpLogin || selectedTab === 0
                  ? staticSections["Group Apps"]
                  : staticSections["3rd Party Apps"]
              ).map(([label, items]) => (
                <Box key={label} sx={{ marginBottom: "8px" }}>
                  <ListItem
                    button
                    onClick={($event) => toggleSection($event, label)}
                    sx={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: "4px",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#2A2C2D",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <ArrowDropUpRounded
                              sx={{
                                transform: openSections[label]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.3s",
                                fontSize: "20px",
                                color: "#12141599",
                              }}
                            />
                            {label}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#2A2C2D",
                              minWidth: "32px",
                              maxWidth: "32px",
                              height: "20px",
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {items.reduce(
                              (total, item) => total + (item.count || 0),
                              0
                            )}
                          </Typography>
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Collapse
                    in={openSections[label]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {items.map((item) => (
                        <ListItem
                          key={item.status}
                          button
                          onClick={async () => {
                            setLoader(true);
                            if (!idpLogin || selectedTab === 0) {
                              // Group Apps tab (or when idpLogin is false, always treat as Group Apps)
                              setActiveStatus(item.status);
                              sessionStorage.setItem(
                                "domain-approver-activeStatus",
                                item.status
                              );
                              getAllApplications(item.status);
                            } else {
                              // 3rd Party Apps tab (only when idpLogin is true)
                              setActiveThirdPartyStatus(item.status);
                              sessionStorage.setItem(
                                "domain-approver-activeThirdPartyStatus",
                                item.status
                              );

                              dispatch(fetchDomainApproverContents());
                            }
                          }}
                          sx={{
                            borderRadius: "4px",
                            bgcolor:
                              ((!idpLogin || selectedTab === 0) &&
                                activeStatus === item.status) ||
                                (idpLogin &&
                                  selectedTab === 1 &&
                                  activeThirdPartyStatus === item.status)
                                ? "#E5F3FF"
                                : "#ffffff",
                            padding: "4px 8px",
                            cursor: "pointer",
                            minHeight: "32px",
                            display: "flex",
                            alignItems: "center",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                fontSize="11px"
                                lineHeight="20px"
                                fontWeight={
                                  ((!idpLogin || selectedTab === 0) &&
                                    activeStatus === item.status) ||
                                    (idpLogin &&
                                      selectedTab === 1 &&
                                      activeThirdPartyStatus === item.status)
                                    ? "600"
                                    : "normal"
                                }
                                color={
                                  ((!idpLogin || selectedTab === 0) &&
                                    activeStatus === item.status) ||
                                    (idpLogin &&
                                      selectedTab === 1 &&
                                      activeThirdPartyStatus === item.status)
                                    ? "#0476E2"
                                    : "#2A2C2D"
                                }
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                                height="20px"
                              >
                                <span
                                  style={{
                                    maxWidth: "200px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flex: 1,
                                    minHeight: "20px",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "16px",
                                      height: "16px",
                                      // Add left margin to align icons with section headers (Beta, Staging, Production)
                                      marginLeft: "16px",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        "& svg path": {
                                          fill: item.status === "DELETED" || activeStatus === item.status
                                            ? "#0476E2"
                                            : "#2A2C2D",
                                          transition: "fill 0.2s ease",
                                        },
                                      }}
                                    >
                                      {item.icon}
                                    </Box>
                                  </span>
                                  {item.text}
                                </span>
                                <span
                                  style={{
                                    display: "flex",
                                    minWidth: "32px",
                                    maxWidth: "32px",
                                    height: "20px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.count}
                                </span>
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </Box>
            <Box sx={{ flexGrow: 1, ml: 2, marginTop: "16px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                width="97%"
              >
                {activeStatus === "DELETED" ? (
                  <Typography sx={{ fontSize: "24px", fontWeight: 600 }}>
                    Deleted Applications
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "20px" }}>
                    Applications -{" "}
                    <span
                      style={{
                        fontSize: "14px",
                        textTransform: "none",
                        color: "#5A5B5C",
                      }}
                    >
                      {formatStatusForDisplay(
                        selectedTab === 0
                          ? activeStatus
                          : activeThirdPartyStatus
                      )}
                    </span>
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search for application name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: "240px" }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FilterAltOutlinedIcon
                    color={
                      appliedFilters.selectedCountries.length > 0 ||
                        appliedFilters.selectedEcus.length > 0
                        ? "primary"
                        : "action"
                    }
                    className="filterIconCls"
                    onClick={toggleDrawer(true)}
                    sx={{
                      cursor: "pointer",
                      ...(appliedFilters.selectedCountries.length > 0 ||
                        (appliedFilters.selectedEcus.length > 0 && {
                          backgroundColor: "#e3f2fd",
                          borderRadius: 1,
                          padding: 0.5,
                        })),
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "97%" }}>
                <DataGrid
                  rows={getFilteredApplications()}
                  columns={getCurrentColumns()}
                  getRowId={(row) => row.id}
                  getRowClassName={(params) => {
                    if (params.row.isGroup) {
                      return "group-row";
                    }
                    if (params.row.isChild) {
                      return "child-row";
                    }
                    return "";
                  }}
                  rowCount={getPaginationCount()}
                  disableColumnMenu
                  checkboxSelection={false}
                  disableRowSelectionOnClick
                  onRowClick={(row) =>
                    activeStatus === "DELETED" ? "" : clickApplications(row)
                  }
                  localeText={{
                    MuiTablePagination: {
                      labelRowsPerPage: "Items per page:",
                    },
                  }}
                  loading={
                    // Only show DataGrid loading for specific cases, not when full-page loader is active
                    !loader &&
                    (selectedTab === 0 && activeStatus === "DELETED"
                      ? deletedApplicationsLoading
                      : false)
                  }
                  slots={{ noRowsOverlay: NoRowsOverlay }}
                  slotProps={{ noRowsOverlay: { showNoAppImage } }}
                  sx={{
                    minWidth: "100%",
                    height: "auto",
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "14px",
                      fontWeight: "600",
                      backgroundColor: "#f0f0f0 !important",
                      color: "#121415",
                      lineHeight: "30px",
                      padding: "0px 0px 0px 4px",
                      display: "flex",
                      alignItems: "center",
                      marginLeft: "2px",
                      height: "40px !important",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "14px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "30px",
                      lineHeight: "30px",
                      padding: "0px",
                    },
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      color: "#5A5B5C",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: "0.25px",
                      display: "flex",
                      alignItems: "center",
                      padding: "0px !important",
                    },
                    "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
                    "& .MuiDataGrid-columnSeparator": { display: "none" },
                    "& .MuiDataGrid-container--top [role=row]": {
                      background: "#f0f0f0 !important",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      marginTop: "0px !important",
                    },
                    "& .MuiDataGrid-virtualScrollerContent": {
                      overflowX: "hidden !important",
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon": {
                      opacity: 1,
                      visibility: "visible",
                    },
                    "& .group-row": {
                      "& .MuiDataGrid-cell": {},
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
          <div>&nbsp;</div>

          {/* DrawerFilter Component - Using memoized version */}
          {memoizedDrawerFilter}

          {/* Error Snackbar */}
          <Snackbar
            open={openErrorPopup}
            autoHideDuration={6000}
            onClose={() => setOpenErrorPopup(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setOpenErrorPopup(false)}
              severity="error"
              sx={{ width: "100%" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>

          {/* Success Snackbar */}
          <Snackbar
            open={openSuccessPopup}
            autoHideDuration={6000}
            onClose={() => setOpenSuccessPopup(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setOpenSuccessPopup(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          {/* ECU Types Popup */}
          <Popover
            open={ecuTypesPopup.open}
            anchorEl={ecuTypesPopup.anchorEl}
            onClose={handleEcuTypesClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  padding: "16px",
                  minWidth: "250px",
                  maxWidth: "400px",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                mb: 2,
                textAlign: "center",
              }}
            >
              ECU Types
            </Typography>
            <Box
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {ecuTypesPopup.data.map((ecuType, index) => (
                <Typography
                  key={`ecu-${ecuType}`}
                  sx={{
                    fontSize: "14px",
                    mb: 1,
                    padding: "6px 8px",
                    borderBottom:
                      index < ecuTypesPopup.data.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    wordWrap: "break-word",
                  }}
                >
                  {ecuType}
                </Typography>
              ))}
            </Box>
          </Popover>

          {/* Countries Popup */}
          <Popover
            open={countriesPopup.open}
            anchorEl={countriesPopup.anchorEl}
            onClose={handleCountriesClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  padding: "16px",
                  minWidth: "250px",
                  maxWidth: "400px",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                mb: 2,
                textAlign: "center",
              }}
            >
              Countries
            </Typography>
            <Box
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {countriesPopup.data.map((country, index) => (
                <Typography
                  key={`country-${country}`}
                  sx={{
                    fontSize: "14px",
                    mb: 1,
                    padding: "6px 8px",
                    borderBottom:
                      index < countriesPopup.data.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    wordWrap: "break-word",
                  }}
                >
                  {country}
                </Typography>
              ))}
            </Box>
          </Popover>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmDialog.open}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">
              Hard Delete - {deleteConfirmDialog.applicationName}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                This action will completely remove the app from the Ignite Store
                databases. That action can not be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleDeleteCancel}
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reject Confirmation Dialog */}
          <Dialog
            open={rejectConfirmDialog.open}
            onClose={handleRejectCancel}
            aria-labelledby="reject-dialog-title"
            aria-describedby="reject-dialog-description"
          >
            <DialogTitle id="reject-dialog-title">Confirm Reject</DialogTitle>
            <DialogContent>
              <DialogContentText id="reject-dialog-description">
                Are you sure you want to reject the delete request for
                application "{rejectConfirmDialog.applicationName}"? This will
                cancel the delete request.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleRejectCancel}
                color="primary"
                variant="outlined"
                disabled={rejectLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectConfirm}
                color="warning"
                variant="contained"
                autoFocus
                disabled={rejectLoading}
              >
                {rejectLoading ? "Rejecting..." : "Reject"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}